import { getStartEpoch } from '@thoughtspot/custom-calendar-service';
import { convertToMilliSeconds, convertToSeconds, } from '@thoughtspot/date-format-util';
import * as dateUtil from '@thoughtspot/date-format-util';
import { isKPIAnomaliesEnabled, isSpotIQV2Enabled, } from '@thoughtspot/session-service';
import { getTimeBucket, hasCustomCalendar, } from '@thoughtspot/visualization-column-util';
import _ from 'lodash';
import { getChartSpecificCustomProps, getCustomColorSelectorArray, getXAxisColumns, } from '../../core/chart-model-utils';
import { chartSpecificConstants } from '../../core/chart-type-specification-service';
import { getSeries } from '../../data/data-util';
import { getIncompleteThreshold, isSparklineEnabled } from './kpi-utils';
export const anomalyColorIndex = 2;
export const boundColorIndex = 3;
export const getCortexData = (chartModel) => {
    return _.get(chartModel, 'data.0.cortexData');
};
export const hasEnoughDataForCortex = (chartModel) => Number(_.get(chartModel, 'data.0.totalRowCount')) > 5;
export const isShowAnomaliesEnabled = (chartModel) => {
    var _a, _b;
    return isKPIAnomaliesEnabled() &&
        isSparklineEnabled(chartModel) &&
        ((_b = (_a = getChartSpecificCustomProps(chartModel)) === null || _a === void 0 ? void 0 : _a.showAnomalies) !== null && _b !== void 0 ? _b : true);
};
export const isShowAnomaliesSupported = (chartModel) => hasEnoughDataForCortex(chartModel) &&
    (!isShowAnomaliesEnabled(chartModel) ||
        !_.isEmpty(getCortexData(chartModel)));
export const isShowBoundsEnabled = (chartModel) => {
    var _a, _b;
    return isShowAnomaliesEnabled(chartModel) &&
        ((_b = (_a = getChartSpecificCustomProps(chartModel)) === null || _a === void 0 ? void 0 : _a.showBounds) !== null && _b !== void 0 ? _b : true);
};
export const hasAnomalyBounds = (chartModel) => {
    var _a, _b, _c, _d;
    return isKPIAnomaliesEnabled() &&
        isSparklineEnabled(chartModel) &&
        ((_b = (_a = getChartSpecificCustomProps(chartModel)) === null || _a === void 0 ? void 0 : _a.showAnomalies) !== null && _b !== void 0 ? _b : true) &&
        ((_d = (_c = getChartSpecificCustomProps(chartModel)) === null || _c === void 0 ? void 0 : _c.showBounds) !== null && _d !== void 0 ? _d : true);
};
export const getAnomalyColor = (chartModel) => _.get(getCustomColorSelectorArray(chartModel), anomalyColorIndex, chartSpecificConstants.DEFAULT_KPI_COLORS[anomalyColorIndex]);
export const getBoundColor = (chartModel) => _.get(getCustomColorSelectorArray(chartModel), boundColorIndex, chartSpecificConstants.DEFAULT_KPI_COLORS[boundColorIndex]);
export const shouldCortexLoadAsync = (chartModel) => {
    var _a;
    return isShowAnomaliesEnabled(chartModel) &&
        !_.isEmpty((_a = getCortexData(chartModel)) === null || _a === void 0 ? void 0 : _a.cacheToken);
};
export const getCortexCacheRequest = (chartModel) => {
    var _a;
    return {
        cacheToken: (_a = getCortexData(chartModel)) === null || _a === void 0 ? void 0 : _a.cacheToken,
    };
};
const boundSeriesTemplate = {
    name: 'Anomaly bounds',
    type: 'arearange',
    visible: true,
    fillOpacity: 0.3,
    lineWidth: 0,
    zIndex: -1,
    enableMouseTracking: false,
};
export const getForecastPoints = (chartModel) => {
    const cortexData = getCortexData(chartModel);
    if (!cortexData ||
        _.isEmpty(cortexData) ||
        _.isEmpty(cortexData.inference)) {
        return undefined;
    }
    return cortexData.inference;
};
export const getCortexPoints = (chartModel) => {
    const cortexData = _.get(chartModel, 'cortexData.forecasts');
    if (!cortexData || _.isEmpty(cortexData) || _.isEmpty(cortexData.matrix)) {
        return undefined;
    }
    return cortexData;
};
export const getCortexAnomalyList = (chartModel, dataModel) => {
    var _a;
    const inferences = getForecastPoints(chartModel);
    if (!inferences) {
        return undefined;
    }
    const data = (_a = getSeries(dataModel)[0]) === null || _a === void 0 ? void 0 : _a.data;
    const times = _(data)
        .mapValues(value => { var _a; return (_a = value === null || value === void 0 ? void 0 : value.xRawValues) === null || _a === void 0 ? void 0 : _a[0]; })
        .values()
        .value();
    let i = 0;
    const anomalyList = _(times)
        .map(time => {
        if ((i === 0 &&
            convertToMilliSeconds(inferences[0].time) !== time) ||
            i >= _.size(inferences)) {
            return false;
        }
        return inferences[i++].anomaly;
    })
        .value();
    return anomalyList;
};
export const getIncompleteThresholdValue = (chartModel, blinkHighchartConfig) => {
    const incompleteZone = blinkHighchartConfig._configOptions.incompleteZone;
    if (incompleteZone.disabled) {
        return undefined;
    }
    const xAxisColumns = getXAxisColumns(chartModel);
    const getDefaultIncompleteThreshold = dateUtil.getTimeBucketStart.bind(this, Date.now());
    const timeBucket = getTimeBucket(xAxisColumns[0]);
    return incompleteZone.getIncompleteThreshold
        ? incompleteZone.getIncompleteThreshold(timeBucket)
        : getDefaultIncompleteThreshold(timeBucket);
};
const getXValueFromOriginalSeriesData = (originalSeriesData, xValue) => {
    var _a;
    return (_a = originalSeriesData.find(seriesData => (seriesData === null || seriesData === void 0 ? void 0 : seriesData.xRawValues[0]) === xValue)) === null || _a === void 0 ? void 0 : _a.x;
};
export const isPointAnomaly = (chartModel, time) => {
    const inferences = getForecastPoints(chartModel);
    if (!inferences || !time) {
        return undefined;
    }
    const timeSecs = convertToSeconds(time);
    const inference = _(inferences).find(infer => Number(infer.time) === timeSecs);
    const incompleteThreshold = getIncompleteThreshold(chartModel);
    return !!(inference === null || inference === void 0 ? void 0 : inference.anomaly) && time <= incompleteThreshold;
};
export const addCortexBoundSeries = (chartModel, blinkHighchartConfig) => {
    var _a, _b, _c, _d, _e;
    const inferences = getForecastPoints(chartModel);
    const forcastPoints = getCortexPoints(chartModel);
    const originalSeriesData = (_a = blinkHighchartConfig._highchartConfig.series[0]) === null || _a === void 0 ? void 0 : _a.data;
    if (isShowBoundsEnabled(chartModel) && inferences && originalSeriesData) {
        const incompleteThreshold = (_b = getIncompleteThresholdValue(chartModel, blinkHighchartConfig)) !== null && _b !== void 0 ? _b : Number.MAX_SAFE_INTEGER;
        const rangeValues = _(inferences)
            .filter(inference => convertToMilliSeconds(inference.time) <=
            incompleteThreshold)
            .mapValues(inference => {
            return [
                getXValueFromOriginalSeriesData(originalSeriesData, convertToMilliSeconds(inference.time)),
                inference.low,
                inference.high,
            ];
        })
            .values()
            .value();
        blinkHighchartConfig._highchartConfig.series.push({
            ...boundSeriesTemplate,
            color: getBoundColor(chartModel),
            data: rangeValues,
        });
    }
    if (forcastPoints && isSpotIQV2Enabled()) {
        const lastChartPoint = chartModel === null || chartModel === void 0 ? void 0 : chartModel.data[0].columnDataLite;
        const xAxisColumn = _.first(getXAxisColumns(chartModel));
        const sortInfo = ((_c = chartModel === null || chartModel === void 0 ? void 0 : chartModel.sortOrder) === null || _c === void 0 ? void 0 : _c.length)
            ? chartModel === null || chartModel === void 0 ? void 0 : chartModel.sortOrder
            : chartModel === null || chartModel === void 0 ? void 0 : chartModel.sortInfo;
        const isXAxisSortAsc = (_e = (_d = sortInfo === null || sortInfo === void 0 ? void 0 : sortInfo.find(_ => _.columnId === xAxisColumn.id)) === null || _d === void 0 ? void 0 : _d.sortAscending) !== null && _e !== void 0 ? _e : true;
        let lastX = isXAxisSortAsc
            ? lastChartPoint[0].dataValue[lastChartPoint[0].dataValue.length - 1]
            : lastChartPoint[0].dataValue[0];
        const lastY = isXAxisSortAsc
            ? lastChartPoint[1].dataValue[lastChartPoint[1].dataValue.length - 1]
            : lastChartPoint[1].dataValue[0];
        const upperIndex = forcastPoints.matrix.columnInfos.findIndex(_column => _column.columnType === 'TARGET_UPPER');
        const lowerIndex = forcastPoints.matrix.columnInfos.findIndex(_column => _column.columnType === 'TARGET_LOWER');
        const timeAxis = forcastPoints.matrix.columnInfos.findIndex(_column => _column.columnType === 'TIME');
        const rangeValues = _(forcastPoints.matrix.rows.slice(0))
            .mapValues(_point => {
            return [
                convertToMilliSeconds(_point.columns[timeAxis].intVal),
                _point.columns[upperIndex].doubleVal,
                _point.columns[lowerIndex].doubleVal,
            ];
        })
            .values()
            .value();
        if (hasCustomCalendar(xAxisColumn)) {
            lastX = getStartEpoch(lastX);
        }
        rangeValues.unshift([convertToMilliSeconds(lastX), lastY, lastY]);
        blinkHighchartConfig._highchartConfig.series.push({
            ...boundSeriesTemplate,
            name: 'Forecast bound',
            color: getBoundColor(chartModel),
            data: rangeValues,
        });
    }
};
export const getCortexPoint = (chartModel, chartPoint) => {
    const inferences = getForecastPoints(chartModel);
    if (!inferences || _.isEmpty(chartPoint === null || chartPoint === void 0 ? void 0 : chartPoint.selectedAttributes)) {
        return undefined;
    }
    const selectedTime = hasCustomCalendar(chartPoint.selectedAttributes[0].column)
        ? getStartEpoch(chartPoint.selectedAttributes[0].value)
        : chartPoint.selectedAttributes[0].value;
    const inference = _(inferences).find(infer => Number(infer.time) === selectedTime);
    const incompleteThreshold = getIncompleteThreshold(chartModel);
    return {
        lowerBound: inference === null || inference === void 0 ? void 0 : inference.low,
        expected: inference === null || inference === void 0 ? void 0 : inference.pred,
        upperBound: inference === null || inference === void 0 ? void 0 : inference.high,
        anomaly: (inference === null || inference === void 0 ? void 0 : inference.anomaly) &&
            convertToMilliSeconds(selectedTime) <= incompleteThreshold,
    };
};
//# sourceMappingURL=cortex-utils.js.map